import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  CloseButton,
  HStack,
  type StyleProps,
  type ToastOptions,
  type UseToastOptions,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";

/** Button under the toast text; clicking it also closes the toast. */
export type ToastAction = {
  label: string;
  onClick: () => void;
};

type Props = {
  onClose: () => void;
  actions?: ToastAction[];
} & Pick<UseToastOptions, "status" | "title" | "description" | "isClosable">;
export const CustomToast = ({
  status,
  title,
  description,
  isClosable,
  onClose,
  actions,
}: Props) => {
  const color = useMemo(() => {
    return getToastColor(status);
  }, [status]);

  return (
    <Alert
      color="black"
      bg="white"
      borderWidth="1px"
      borderColor="gray.300"
      rounded="xl"
      status={status}
    >
      <HStack align="start" justify="start" w="full">
        <AlertIcon color={color} />
        <VStack align="start" w="full">
          <AlertTitle>{title}</AlertTitle>
          {description && <AlertDescription>{description}</AlertDescription>}
          {actions && actions.length > 0 && (
            <HStack flexWrap="wrap" spacing="2">
              {actions.map((action) => (
                <Button
                  key={action.label}
                  colorScheme="violet"
                  onClick={() => {
                    action.onClick();
                    onClose();
                  }}
                  size="xs"
                  variant="outline"
                >
                  {action.label}
                </Button>
              ))}
            </HStack>
          )}
        </VStack>
      </HStack>
      {isClosable && <CloseButton color="gray.400" onClick={onClose} />}
    </Alert>
  );
};

function getToastColor(
  status: ToastOptions["status"] = "default",
): StyleProps["color"] {
  switch (status) {
    case "error":
      return "red.500";
    case "warning":
      return "orange.300";
    case "success":
      return "green.600";
    default:
      return "grey.500";
  }
}
